import React from "react";

import { Autorenew } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import clsx from "clsx";

import mainStyles from "../../styles";
import DisplayHeading from "./display-heading";

const dialogStyles = {
  paper: {
    overflowY: "unset",
  },
  title: {
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
};

interface GenericDialogProps {
  title: string;
  children: React.ReactNode;
  // controls whether dialog is open or not
  open: boolean;
  // Function called upon close of dialog, use to toggle `open` boolean.
  // E.g.
  // const handleClose = () => {
  //   setOpen(false);
  // };
  handleDialogClose: () => void;
  // If true, it renders an `X` icon button in the top right of dialog
  hasXCloseBtn?: boolean;
  // If true, renders acknowledge button, e.g. 'Ok'
  hasAckBtn?: boolean;
  // If need to perform some action on click, call fn here
  // Will default to using handleDialogClose if no fn provided
  handleAckBtnClick?: () => void;
  // If value provided, will be the text on the primary btn instead of 'ok'
  acknowledgeBtnLabel?: string;
  // Handles toggling of ackBtn disabled prop
  disabled?: boolean;
  width?: false | "sm" | "xs" | "md" | "lg" | "xl" | undefined;
  // If true, will show a loading spinner in the button
  isLoading?: boolean;
}

const GenericDialog = ({
  title,
  children,
  open,
  handleDialogClose,
  hasXCloseBtn,
  hasAckBtn,
  handleAckBtnClick,
  acknowledgeBtnLabel,
  disabled,
  width,
  isLoading,
}: GenericDialogProps) => {
  const mainClasses = mainStyles();

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="generic-dialog"
      data-testid="generic-dialog"
      fullWidth={true}
      maxWidth={width !== undefined ? width : "sm"}
      PaperProps={{
        sx: dialogStyles.paper,
      }}
    >
      <DialogTitle
        id="confirm-dialog"
        sx={{ paddingBottom: "0", ...dialogStyles.title }}
      >
        <Grid
          container
          justifyContent="center"
          marginTop={2}
          sx={hasXCloseBtn ? { paddingRight: 5 } : undefined}
        >
          <DisplayHeading>{title}</DisplayHeading>
        </Grid>
        {hasXCloseBtn && (
          <IconButton
            sx={dialogStyles.closeButton}
            onClick={handleDialogClose}
            data-testid="close-dialog"
            size="medium"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: "0" }}>
        <Grid container justifyContent="center">
          {children}
        </Grid>
      </DialogContent>
      <DialogActions>
        {hasAckBtn && (
          <Grid
            container
            justifyContent="right"
            marginBottom={2}
            marginRight={2}
          >
            <Button
              variant="contained"
              disabled={disabled || isLoading}
              onClick={
                !!handleAckBtnClick ? handleAckBtnClick : handleDialogClose
              }
              color="primary"
              data-testid="generic-dialog-ack-btn"
            >
              {isLoading && (
                <Autorenew
                  data-testid="loading-spinner"
                  className={clsx("spin-1s", mainClasses.spaceRightXSmall)}
                />
              )}
              {acknowledgeBtnLabel || "Ok"}
            </Button>
          </Grid>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenericDialog;
